import { tryURL } from '@aigc/toolkit';
import { App, Button, Upload } from 'antd';
import { useEffect, useState, type ComponentProps } from 'react';
import { createBeforeUpload, createUploadValidator } from '../features/file';
import { queryUploadedFiles, uploadFile } from '../features/file/api';
import type { FileDto, UploadFileOutputDto } from '../features/file/model';

export const ProductImageSelectionModal = ({
  isOpen,
  onClose,
  onConfirm,
  currentImage,
  defaultImage,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (image: UploadFileOutputDto) => void;
  currentImage: UploadFileOutputDto | null;
  defaultImage?: string;
}) => {
  const { message, notification } = App.useApp();
  const [activeTab, setActiveTab] = useState<string>('upload');
  const [productImages, setProductImages] = useState<FileDto[]>([]);
  const [selectedImage, setSelectedImage] =
    useState<UploadFileOutputDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab('upload');
      setSelectedImage(null);
      return;
    }

    setSelectedImage(currentImage);
    loadProductImages();
  }, [isOpen, currentImage]);

  const loadProductImages = async () => {
    setLoading(true);
    try {
      const data = await queryUploadedFiles();
      setProductImages(data.items);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload: ComponentProps<'input'>['onChange'] = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const result = await createBeforeUpload({
      validator: createUploadValidator({ accept: '.jpg,.jpeg,.png' }),
      onError: ({ title, message }) => {
        notification.error({ title, description: message });
      },
    })(file);
    if (result === Upload.LIST_IGNORE) return;

    if (file.size > 5 * 1024 * 1024) {
      notification.error({
        title: '错误',
        description: '图片大小不能超过5M',
      });
      return;
    }

    const data = await uploadFile(file);
    setSelectedImage(data);
    message.success('图片上传成功！');
    loadProductImages();
  };

  const handleConfirm = () => {
    if (!selectedImage) {
      message.info('请选择一张产品图');
      return;
    }
    onConfirm(selectedImage);
    onClose();
  };

  const isDefaultImage = (image: UploadFileOutputDto) => {
    return defaultImage && image.file_url === defaultImage;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="flex max-h-[80vh] w-full max-w-3xl animate-fade-in flex-col overflow-hidden rounded-2xl border border-primary-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-primary-200 p-6">
          <h2 className="text-xl font-bold text-primary-800">选择产品图</h2>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-300 transition-colors hover:bg-primary-200 hover:text-primary-900"
          >
            ×
          </button>
        </div>

        <div className="flex border-b border-primary-200">
          <button
            onClick={() => setActiveTab('upload')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'upload'
                ? 'border-b-2 border-primary-900 bg-primary-900/5 text-primary-500'
                : 'text-primary-500 hover:bg-primary-100/50 hover:text-primary-900'
            }`}
          >
            📤 上传新产品图
          </button>
          <button
            onClick={() => setActiveTab('select')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'select'
                ? 'border-b-2 border-primary-900 bg-primary-900/5 text-primary-500'
                : 'text-primary-500 hover:bg-primary-100/50 hover:text-primary-900'
            }`}
          >
            🖼️ 从列表选择
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'upload' && (
            <div className="space-y-4">
              <div className="group cursor-pointer rounded-xl border-2 border-dashed border-primary-200 p-8 text-center transition-colors hover:border-primary-900/50">
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={handleUpload}
                  className="w-full cursor-pointer text-sm text-primary-500 transition-all file:mr-4 file:rounded-lg file:border-0 file:bg-primary-900 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-primary-900"
                />
                <p className="mt-3 text-xs text-primary-500">
                  💡 建议白底图，清晰可见产品特征，支持jpg、png格式，最大5M
                </p>
                {selectedImage?.filename && (
                  <div className="mt-4">
                    <p className="mb-2 text-sm text-success-400">
                      ✅ 已选择：{selectedImage.filename}
                    </p>
                    <div className="relative inline-block">
                      <img
                        src={selectedImage.file_url}
                        alt="预览"
                        className="max-h-[200px] max-w-full rounded-lg border border-primary-200 object-contain"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'select' && (
            <div>
              {loading ? (
                <div className="py-12 text-center text-primary-500">
                  <p>加载中...</p>
                </div>
              ) : productImages.length === 0 ? (
                <div className="py-12 text-center text-primary-500">
                  <p>📭 暂无产品图</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                  {productImages.map((file) => {
                    const image: UploadFileOutputDto = {
                      id: file.id,
                      mime: file.mime,
                      filename: file.filename,
                      file_url:
                        tryURL(
                          file.oss_object_key,
                          file.oss_host,
                        )?.toString() ?? '',
                      created_at: file.created_at,
                    };
                    const isDefault = isDefaultImage(image);

                    return (
                      <div
                        key={image.id}
                        onClick={() => setSelectedImage(image)}
                        className={`relative cursor-pointer overflow-hidden rounded-lg border-2 transition-all hover:scale-105 ${
                          selectedImage?.filename === image.filename
                            ? 'border-primary-900 ring-2 ring-primary-900/30'
                            : 'border-primary-200 hover:border-primary-900/50'
                        } ${isDefault ? 'bg-primary-900/10' : ''}`}
                      >
                        <div className="relative aspect-square">
                          <img
                            src={image.file_url}
                            alt={image.filename}
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div className="bg-white/90 p-2 backdrop-blur">
                          <p className="truncate text-xs text-primary-300">
                            {image.filename}
                          </p>
                        </div>
                        {isDefault && (
                          <div className="absolute right-2 top-2">
                            <span className="rounded-full bg-primary-900 px-2 py-1 text-xs font-medium text-white">
                              默认
                            </span>
                          </div>
                        )}
                        {selectedImage?.filename === image.filename &&
                          !isDefault && (
                            <div className="absolute right-2 top-2">
                              <span className="rounded-full bg-success-500 px-2 py-1 text-xs font-medium text-white">
                                选中
                              </span>
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {selectedImage?.filename && (
            <div className="mt-6 rounded-xl bg-primary-100 p-4">
              <p className="mb-2 text-sm font-medium text-primary-800">
                已选择：
              </p>
              {selectedImage.filename && (
                <div className="flex items-center gap-4">
                  <img
                    src={selectedImage.file_url}
                    alt={selectedImage.filename}
                    className="h-16 w-16 rounded-lg border border-primary-200 object-cover"
                  />
                  <div>
                    <p className="text-sm text-primary-300">
                      {selectedImage.filename}
                    </p>
                    {isDefaultImage(selectedImage) && (
                      <p className="text-xs text-primary-500">当前默认产品图</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-3 border-t border-primary-200 p-6">
          <Button onClick={onClose} color="default" variant="filled">
            取消
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedImage}
            color="primary"
            variant="solid"
          >
            确定
          </Button>
        </div>
      </div>
    </div>
  );
};
